import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { Spin } from 'antd'
import { ExploreExpensesLevel } from '../../components'
import { buildCacheKey, snakeCaseToTitle } from '../../util'
import { useExpensesQuery } from '../../hooks'
import constants from '../../constants'
import api from '../../api'
import { INewEntity } from '../../typings/entities/entity'

export interface IRouterParams {
	institution?: string
	entity?: string
	program?: string
	year?: string
	quarter?: string
}

const ExploreExpensesPage: React.FC = () => {
	const [query] = useExpensesQuery()
	const params = useParams<{ entity?: string }>()
	const location = useLocation()
	const aggregationCacheKey = buildCacheKey('expense', 'aggregation', {
		query,
		extraDependencies: [params],
	})

	const isRootRoute = location.pathname === '/gastos'
	const entityID = location.pathname.split('/')[2]
	const { data: entity } = useSWR<INewEntity>(
		isRootRoute
			? 'undefined'
			: buildCacheKey('expense', 'discrete', { documentID: entityID })
	)

	const { data: aggregationData } = useSWR<Charts.TreemapData | undefined>(
		aggregationCacheKey,
		async () => {
			try {
				const response = await api.get('/expenses/aggregate', {
					params: {
						belongingTo: params.entity,
						type: query.match.type,
						destinationType: query.match.destinationType,
						destinationNature: query.match.destinationNature,
						from: query.match.from,
						to: query.match.to,
						amount:
							Object.keys(query.match.amount || {}).length === 2
								? {
										$gte: query.match.amount?.gte,
										$lte: query.match.amount?.lte,
								  }
								: undefined,
					},
				})

				const processedData: Charts.TreemapData = {
					title: response.data?.length
						? 'Explorar Gastos'
						: entity
						? `La entidad "${entity.name}" no tiene sub-entidades o sus sub-entidades no han registrado gastos que cumplan con los parámetros de búsqueda específicados.`
						: 'No se encontraron gastos que cumplan con los parámetros de búsqueda específicados.',
					size: 0,
					children: response.data.map((entry: any, i: number) => ({
						...entry,
						title: `${snakeCaseToTitle(`${entry.title}`) || ''} ($${
							entry.size
						})`,
						realTitle: entry.title, // this has to be done because react-vis has its own prop "title" which is the that is displayed, but we still need access to the real aggregation title to perfom the WS-triggered update
						color: constants.moduloToColorMap[i % 16],
					})),
					style: {
						display: 'flex',
						border: '1px solid #f7f7f7',
						cursor: 'pointer',
					},
					color: '#f7f7f7',
				}

				return processedData
			} catch (error) {
				console.error(error)
			}
		},
		{
			focusThrottleInterval: 10000,
		}
	)

	return (
		<div>
			<Spin spinning={!aggregationData}>
				{aggregationData && <ExploreExpensesLevel data={aggregationData} />}
			</Spin>
		</div>
	)
}

export default ExploreExpensesPage
